import { Component, computed, input } from '@angular/core';

import { InfoscreenWeather } from '../infoscreen-module.types';
import { WEATHER_LABELS, WeatherIconComponent } from './weather-icon.component';

/** "14" — the hour, for the forecast. */
export function hourLabel(time: string): string {
  return String(new Date(time).getHours()).padStart(2, '0');
}

/** "11°" — whole degrees, as on a weather sign. */
export function degrees(value: number): string {
  return `${Math.round(value)}°`;
}

/**
 * The forecast as an ad: the weather now, large, and the next twelve hours in steps of three — in
 * the ad band and among the welcome pane's ads, when the screen shows the weather "as an ad".
 */
@Component({
  selector: 'app-weather-card',
  imports: [WeatherIconComponent],
  template: `
    <div class="now">
      <app-weather-icon class="big" [condition]="weather().condition" [night]="weather().night" />
      <div>
        <div class="temp">{{ degrees(weather().temperature) }}</div>
        <div class="label">{{ label() }}</div>
      </div>
    </div>
    <div class="hours">
      @for (hour of next(); track hour.time) {
        <div class="hour">
          <div class="at">{{ hourLabel(hour.time) }}</div>
          <app-weather-icon [condition]="hour.condition" [night]="hour.night" />
          <div class="t">{{ degrees(hour.temperature) }}</div>
        </div>
      }
    </div>
    <div class="source">Vejr: {{ weather().source }}</div>
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: 1.2em;
      padding: 0.5em 1.2em 0.9em;
      box-sizing: border-box;
      border-radius: 0.4em;
      background: linear-gradient(160deg, #3d8fd1 0%, #1f5d93 100%);
      color: #ffffff;
      text-shadow: none;
      white-space: nowrap;
    }
    .now {
      display: flex;
      align-items: center;
      gap: 0.4em;
    }
    .big {
      font-size: 2.6em;
    }
    .temp {
      font-size: 1.9em;
      font-weight: 700;
      line-height: 1;
    }
    .label {
      font-size: 0.8em;
      opacity: 0.9;
    }
    .hours {
      display: flex;
      gap: 0.9em;
      font-size: 0.85em;
    }
    .hour {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.15em;
      app-weather-icon {
        font-size: 1.5em;
      }
    }
    .at {
      opacity: 0.85;
    }
    .t {
      font-weight: 700;
    }
    .source {
      position: absolute;
      right: 0.8em;
      bottom: 0.2em;
      font-size: 0.5em;
      opacity: 0.75;
    }
  `,
})
export class WeatherCardComponent {
  readonly weather = input.required<InfoscreenWeather>();

  readonly degrees = degrees;
  readonly hourLabel = hourLabel;
  readonly label = computed(() => WEATHER_LABELS[this.weather().condition]);
  /** In 3, 6, 9 and 12 hours. */
  readonly next = computed(() => [2, 5, 8, 11].map((i) => this.weather().hours[i]).filter((h) => !!h));
}
